import classNames from 'classnames'
import { shallowEqual } from 'react-redux'
import { useEffect, useState } from 'react'

import Icon from 'project/Icon'
import Card from './base/Card'
import Address from './Address'
import { toast } from './base/Toast'
import Input from './base/forms/Input'
import Button from './base/forms/Button'
import ModalDefault from './base/Modal'
import { ErrorMessage } from './base/Messages'
import Checkbox from './base/forms/Checkbox'
import { Address as AddressType } from 'common/types/requests'
import { useChangeForwardAddressMutation } from 'common/services/useAddress'
import { useDefaultSubscription } from 'common/services/useDefaultSubscription'

interface Address {
  address_line_1?: string
  address_line_2?: string
  city?: string
  postal_code?: string
  country?: string
}

const parseAddress = (address: string | Address): string => {
  if (!address) return ''
  if (typeof address === 'string') return address

  const parts: string[] = []
  if (address.address_line_1) parts.push(address.address_line_1)
  if (address.address_line_2) parts.push(address.address_line_2)
  if (address.city) parts.push(address.city)
  if (address.postal_code) parts.push(address.postal_code)
  if (address.country) parts.push(address.country)

  return parts.join(',\n')
}

const InputStyle = {
  lineHeight: 1.5,
  minHeight: 102,
}

interface Props {}

const ChangeAddress: React.FC<Props> = ({}) => {
  const { subscriptionDetail } = useDefaultSubscription()
  const address = subscriptionDetail?.address
  const [changeAddress, { error, isLoading }] = useChangeForwardAddressMutation(
    {},
  )
  const [selectedAddress, setSelectedAddress] = useState(address)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  useEffect(() => {
    setSelectedAddress(address)
  }, [address])

  const hasChanged = !shallowEqual(address, selectedAddress)
  const isAddressValid =
    !!selectedAddress?.address_line_1 &&
    !!selectedAddress?.city &&
    !!selectedAddress?.postal_code &&
    !!selectedAddress?.country

  const handleChangeAddress = () => {
    changeAddress({
      forwarding_address: selectedAddress as AddressType,
      subscription_id: subscriptionDetail?.id?.toString() ?? '',
    }).then((res) => {
      if ('data' in res) {
        toast(null, 'Forwarding address updated')
        setIsModalOpen(false)
        setIsConfirmed(false)
      }
    })
  }

  const onCancelModal = () => {
    setIsModalOpen(false)
    setIsConfirmed(false)
    setSelectedAddress(address)
  }

  return (
    <>
      <Card>
        <h2 className='mb-0 fw-semibold mb-4'>Forwarding Address</h2>
        <Address
          onChange={(v) => {
            setSelectedAddress(v)
          }}
          name={'address'}
          title={''}
          value={selectedAddress}
          actionButtons={
            <div className='justify-content-end d-flex gap-3'>
              <Button
                className={classNames('px-10', {
                  'opacity-0 pointer-events-none': !hasChanged,
                })}
                theme='secondary'
                onClick={() => setSelectedAddress(address)}
                type='button'
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={() => setIsModalOpen(true)}
                className='px-10'
                disabled={isLoading || !hasChanged || !isAddressValid}
              >
                Update
              </Button>
            </div>
          }
        />
      </Card>
      <ModalDefault
        isOpen={isModalOpen}
        onDismiss={onCancelModal}
        contentClassName='px-4 py-7'
        padding={0}
        centered
        toggle={onCancelModal}
      >
        <h3 className='fw-bold'>Update Forwarding Address</h3>
        <h4 className='mb-2 fw-semibold'>Previous Address</h4>
        <Input
          style={InputStyle}
          textarea
          disabled
          className='flex-1'
          inputClassName='py-2'
          value={parseAddress(address)}
        />

        <h4 className='mt-3 mb-2 fw-semibold'>New Address</h4>
        <Input
          style={InputStyle}
          textarea
          disabled
          className='flex-1'
          inputClassName='py-2'
          value={parseAddress(selectedAddress)}
        />
        <hr className='my-7' />
        <div className='gap-3 ps-3 pe-2 py-3 pb-5 bg-light300 rounded-2'>
          <div className='flex-row gap-2 align-items-center'>
            <Icon fill='danger' name={'error'} />
            <span className='text-xxs text-grey fw-bold'>
              Confirmation for Forwarding Address Update
            </span>
          </div>
          <div
            onClick={() => setIsConfirmed(!isConfirmed)}
            className='flex-row justify-content-start align-items-start gap-2 mt-3 cursor-pointer'
          >
            <Checkbox checked={isConfirmed} label='' />
            <span className='text-xxs text-grey'>
              By checking this box, I confirm that the address provided is a
              valid UK address, is my current primary residence, is{' '}
              <strong>not</strong> a commercial address and is{' '}
              <strong>not</strong> the address of another virtual office or mail
              forwarding service.
            </span>
          </div>
        </div>

        <ErrorMessage>{error}</ErrorMessage>
        <div className='flex-row gap-3 justify-content-end mt-10'>
          <Button className='px-10' theme='secondary' onClick={onCancelModal}>
            Cancel
          </Button>
          <Button
            className='px-10'
            onClick={handleChangeAddress}
            disabled={!isConfirmed || isLoading}
          >
            Confirm
          </Button>
        </div>
      </ModalDefault>
    </>
  )
}

export default ChangeAddress
